import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Flag, Menu, X, ChevronDown, Sun, Moon, Shield, User, LogIn, Bot, Users, Home, BookOpen, DollarSign, BarChart2, Scale } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";

const TOOLS = [
  { label: "Party Match Quiz", path: "/quiz", desc: "Find your party" },
  { label: "Compare Parties", path: "/compare", desc: "Side-by-side" },
  { label: "Impact Calculator", path: "/impact", desc: "How policies affect you" },
  { label: "Fact Checker", path: "/fact-check", desc: "Verify claims" },
  { label: "Poll Tracker", path: "/polls", desc: "Live polling data" },
  { label: "Issue Deep Dives", path: "/issues", desc: "Key topics explained" },
  { label: "Historical Results", path: "/history", desc: "Past elections" },
  { label: "Electorate Results", path: "/electorate", desc: "How your area voted" },
  { label: "Political Glossary", path: "/glossary", desc: "Know the jargon" },
  { label: "Policy Scorecard", path: "/scorecard", desc: "Track party promises", divider: true },
  { label: "News Feed", path: "/news", desc: "AI political briefs" },
  { label: "Voting Simulator", path: "/simulator", desc: "How preferences flow" },
  { label: "They Vote For You", path: "/politicians", desc: "Track politician votes", divider: true },
  { label: "Compare Politicians", path: "/politicians/compare", desc: "Side-by-side comparison" },
  { label: "Policy Votes", path: "/policies", desc: "See how MPs voted on bills" },
  { label: "Campaign Promises", path: "/promises", desc: "Track promises kept & broken" },
  { label: "Leaderboard", path: "/leaderboard", desc: "Who delivers on promises?", divider: true },
  { label: "Follow the $$$", path: "/funding", desc: "OzPol Transparent — who funds who?", divider: true },
  { label: "SA 2026 Election", path: "/sa-2026", desc: "March 21 — Full guide & countdown", divider: false },
  { label: "VIC 2026 Election", path: "/vic-2026", desc: "November 28 — Allan vs Pesutto guide", divider: false },
  { label: "NSW 2027 Election", path: "/nsw-2027", desc: "March 27, 2027 — Minns vs Speakman", divider: false },
  { label: "Federal Election 2025", path: "/federal-2025", desc: "Labor majority — full results guide", divider: true },
];

const MOBILE_QUICK = [
  { label: "Home", path: "/", Icon: Home },
  { label: "AI Chat", path: "/chat", Icon: Bot, iconColor: "#A855F7" },
  { label: "How to Vote", path: "/how-to-vote", Icon: BookOpen },
  { label: "Follow the $$$", path: "/funding", Icon: DollarSign, iconColor: "#F59E0B" },
];

const ELECTION_GUIDES = [
  { label: "SA 2026", path: "/sa-2026", desc: "March 21 · Full guide & countdown", color: "#E11D48" },
  { label: "VIC 2026", path: "/vic-2026", desc: "November 28 · Allan vs Pesutto", color: "#2563EB" },
  { label: "NSW 2027", path: "/nsw-2027", desc: "March 27, 2027 · Minns vs Speakman", color: "#10B981" },
  { label: "Federal 2025", path: "/federal-2025", desc: "Labor majority · Full results", color: "#F59E0B" },
];

const RESEARCH_TOOLS = [
  "/quiz", "/compare", "/impact", "/fact-check", "/polls",
  "/issues", "/history", "/electorate", "/glossary", "/scorecard", "/news", "/simulator",
];

const ACCOUNTABILITY_TOOLS = [
  "/politicians", "/politicians/compare", "/policies", "/promises", "/leaderboard",
];

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggle } = useTheme();
  const { user } = useAuth();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setToolsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const isToolActive = TOOLS.some((t) => location.pathname === t.path);

  const navBg = isDark
    ? "rgba(8, 14, 26, 0.88)"
    : "rgba(255, 255, 255, 0.88)";
  const navBorder = isDark
    ? "rgba(51, 65, 85, 0.5)"
    : "rgba(226, 232, 240, 0.9)";
  const textColor = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#64748B";
  const dropdownBg = isDark ? "#111827" : "#FFFFFF";
  const dropdownBorder = isDark ? "rgba(51,65,85,0.6)" : "rgba(226,232,240,0.9)";
  const hoverBg = isDark ? "rgba(51,65,85,0.35)" : "rgba(241,245,249,0.9)";

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const navBtnStyle = (paths, special) => {
    const active = paths.some(isActive);
    if (special === "chat") {
      return {
        background: active ? "rgba(139,92,246,0.15)" : "transparent",
        color: active ? "#A855F7" : textColor,
        fontWeight: active ? "600" : "500",
      };
    }
    return {
      background: active ? (isDark ? "rgba(16,185,129,0.12)" : "rgba(13,92,58,0.08)") : "transparent",
      color: active ? (isDark ? "#34d399" : "#0D5C3A") : textColor,
      fontWeight: active ? "600" : "500",
    };
  };

  return (
    <header
      className="sticky top-0 z-50 border-b transition-all duration-300"
      style={{
        backgroundColor: navBg,
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderColor: navBorder,
      }}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">

        {/* Logo */}
        <button
          data-testid="logo-btn"
          onClick={() => navigate("/")}
          className="flex items-center gap-2.5 group flex-shrink-0"
          style={{ background: "none", border: "none", cursor: "pointer" }}
        >
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center transition-transform group-hover:scale-105"
            style={{
              background: "linear-gradient(135deg, #0D5C3A 0%, #10B981 100%)",
              boxShadow: "0 4px 12px rgba(13,92,58,0.30)",
            }}
          >
            <Flag className="w-4.5 h-4.5 text-white" style={{ width: "18px", height: "18px" }} />
          </div>
          <span
            className="text-xl font-black tracking-tight"
            style={{ fontFamily: "Outfit, sans-serif", color: textColor }}
          >
            AusVote
            <span style={{ color: "#EAB308" }}>.</span>
          </span>
        </button>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-0.5">

          <button
            data-testid="nav-home"
            onClick={() => navigate("/")}
            className="px-3.5 py-2 rounded-lg text-sm transition-all duration-200"
            style={navBtnStyle(["/"])}
          >
            Home
          </button>

          {/* Tools Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              data-testid="tools-dropdown-btn"
              onClick={() => setToolsOpen(!toolsOpen)}
              className="flex items-center gap-1 px-3.5 py-2 rounded-lg text-sm transition-all duration-200"
              style={navBtnStyle(TOOLS.map((t) => t.path))}
            >
              Tools
              <ChevronDown
                style={{ width: 13, height: 13 }}
                className={`transition-transform duration-200 ${toolsOpen ? "rotate-180" : ""}`}
              />
            </button>

            {toolsOpen && (
              <div
                className="absolute top-full left-0 mt-2 overflow-hidden"
                style={{
                  backgroundColor: dropdownBg,
                  border: `1px solid ${dropdownBorder}`,
                  borderRadius: "16px",
                  boxShadow: isDark
                    ? "0 20px 50px -8px rgba(0,0,0,0.6), 0 0 0 1px rgba(51,65,85,0.3)"
                    : "0 20px 50px -8px rgba(15,23,42,0.15), 0 0 0 1px rgba(226,232,240,0.8)",
                  width: "280px",
                  zIndex: 60,
                  padding: "6px",
                }}
              >
                {TOOLS.map((tool) => (
                  <div key={tool.path}>
                    {tool.divider && (
                      <div
                        className="my-1 mx-2"
                        style={{
                          borderTop: `1px solid ${isDark ? "rgba(51,65,85,0.4)" : "rgba(226,232,240,0.8)"}`,
                        }}
                      />
                    )}
                    <button
                      data-testid={`dropdown-${tool.path.slice(1)}`}
                      onClick={() => { navigate(tool.path); setToolsOpen(false); }}
                      className="w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 group"
                      style={{ background: "transparent" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                    >
                      <div className="font-semibold text-sm" style={{ color: textColor }}>{tool.label}</div>
                      <div className="text-xs mt-0.5" style={{ color: textSec }}>{tool.desc}</div>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            data-testid="nav-chat"
            onClick={() => navigate("/chat")}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm transition-all duration-200"
            style={navBtnStyle(["/chat"], "chat")}
          >
            <Bot style={{ width: 14, height: 14 }} />
            AI Chat
          </button>

          <button
            data-testid="nav-politicians"
            onClick={() => navigate("/politicians")}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm transition-all duration-200"
            style={navBtnStyle(["/politicians"])}
          >
            <Users style={{ width: 14, height: 14 }} />
            They Vote
          </button>

          <button
            data-testid="nav-how-to-vote"
            onClick={() => navigate("/how-to-vote")}
            className="px-3.5 py-2 rounded-lg text-sm transition-all duration-200"
            style={navBtnStyle(["/how-to-vote"])}
          >
            How to Vote
          </button>

          {/* Divider */}
          <div
            className="w-px h-5 mx-1"
            style={{ backgroundColor: isDark ? "rgba(51,65,85,0.5)" : "rgba(226,232,240,0.9)" }}
          />

          {/* Dark mode toggle */}
          <button
            data-testid="dark-mode-toggle"
            onClick={toggle}
            className="p-2 rounded-lg transition-all duration-200"
            title={isDark ? "Light mode" : "Dark mode"}
            style={{ background: isDark ? "rgba(234,179,8,0.12)" : "rgba(15,23,42,0.06)", color: isDark ? "#FACC15" : "#475569" }}
          >
            {isDark ? <Sun style={{ width: 15, height: 15 }} /> : <Moon style={{ width: 15, height: 15 }} />}
          </button>

          {/* Admin */}
          <button
            data-testid="nav-admin"
            onClick={() => navigate("/admin")}
            className="p-2 rounded-lg transition-all duration-200"
            title="Admin Panel"
            style={{ background: isDark ? "rgba(51,65,85,0.3)" : "rgba(226,232,240,0.6)", color: isDark ? "#64748B" : "#94A3B8" }}
          >
            <Shield style={{ width: 15, height: 15 }} />
          </button>

          {/* Login / Dashboard */}
          {user ? (
            <button
              data-testid="nav-dashboard-btn"
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ml-1"
              style={{
                background: "linear-gradient(135deg, #0D5C3A 0%, #10B981 100%)",
                color: "#fff",
                boxShadow: "0 4px 12px rgba(13,92,58,0.28)",
              }}
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="w-5 h-5 rounded-full" />
              ) : (
                <User style={{ width: 14, height: 14 }} />
              )}
              {user.name?.split(" ")[0]}
            </button>
          ) : (
            <button
              data-testid="nav-login-btn"
              onClick={() => navigate("/auth")}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ml-1"
              style={{
                background: isDark ? "rgba(234,179,8,0.12)" : "#0F172A",
                color: isDark ? "#FACC15" : "#FFFFFF",
                boxShadow: isDark ? "none" : "0 4px 12px rgba(15,23,42,0.22)",
              }}
            >
              <LogIn style={{ width: 14, height: 14 }} />
              Login
            </button>
          )}

          {/* AEC */}
          <a
            href="https://www.aec.gov.au"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="aec-link"
            className="ml-1 px-3.5 py-2 rounded-xl text-xs font-bold transition-all duration-200"
            style={{
              background: "linear-gradient(135deg, #0D5C3A 0%, #1a7a52 100%)",
              color: "#fff",
              boxShadow: "0 2px 8px rgba(13,92,58,0.22)",
            }}
          >
            AEC
          </a>
        </nav>

        {/* Mobile controls */}
        <div className="lg:hidden flex items-center gap-2">
          <button
            onClick={toggle}
            className="p-2 rounded-lg transition-all"
            style={{ background: isDark ? "rgba(234,179,8,0.12)" : "rgba(15,23,42,0.06)", color: isDark ? "#FACC15" : "#475569" }}
            data-testid="dark-mode-toggle-mobile"
          >
            {isDark ? <Sun style={{ width: 16, height: 16 }} /> : <Moon style={{ width: 16, height: 16 }} />}
          </button>
          <button
            className="p-2 rounded-lg transition-all"
            style={{ background: isDark ? "rgba(51,65,85,0.4)" : "rgba(226,232,240,0.7)", color: textColor }}
            data-testid="mobile-menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X style={{ width: 18, height: 18 }} /> : <Menu style={{ width: 18, height: 18 }} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu — animated, grouped, full coverage */}
      <div
        className="lg:hidden overflow-hidden"
        style={{
          maxHeight: menuOpen ? "82vh" : "0",
          transition: "max-height 0.32s cubic-bezier(0.4, 0, 0.2, 1)",
          borderTop: menuOpen ? `1px solid ${navBorder}` : "none",
          backgroundColor: isDark ? "rgba(8,14,26,0.98)" : "rgba(255,255,255,0.98)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
        }}
        data-testid="mobile-menu"
      >
        <div className="overflow-y-auto" style={{ maxHeight: "82vh" }}>

          {/* ── Quick Access Grid ── */}
          <div className="px-4 pt-4 pb-3 grid grid-cols-2 gap-2">
            {MOBILE_QUICK.map((item) => (
              <button
                key={item.path}
                onClick={() => { navigate(item.path); setMenuOpen(false); }}
                data-testid={`mobile-quick-${item.path.replace(/\//g, "") || "home"}`}
                className="flex items-center gap-2.5 px-3.5 rounded-xl text-sm font-semibold text-left transition-all"
                style={{
                  minHeight: 52,
                  background: isActive(item.path)
                    ? (isDark ? "rgba(16,185,129,0.18)" : "rgba(13,92,58,0.09)")
                    : (isDark ? "rgba(30,41,59,0.8)" : "rgba(241,245,249,0.9)"),
                  color: isActive(item.path) ? (isDark ? "#34d399" : "#0D5C3A") : textColor,
                  border: `1px solid ${isActive(item.path)
                    ? (isDark ? "rgba(52,211,153,0.3)" : "rgba(13,92,58,0.18)")
                    : (isDark ? "rgba(51,65,85,0.45)" : "rgba(226,232,240,0.9)")}`,
                }}
              >
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{
                    background: isActive(item.path)
                      ? (isDark ? "rgba(52,211,153,0.15)" : "rgba(13,92,58,0.1)")
                      : (isDark ? "rgba(51,65,85,0.5)" : "rgba(226,232,240,0.8)"),
                  }}
                >
                  <item.Icon
                    style={{
                      width: 14, height: 14,
                      color: isActive(item.path) ? (isDark ? "#34d399" : "#0D5C3A") : (item.iconColor || (isDark ? "#94A3B8" : "#64748B")),
                    }}
                  />
                </div>
                {item.label}
              </button>
            ))}
          </div>

          {/* ── Election Guides ── */}
          <div className="px-4 pb-3" style={{ borderTop: `1px solid ${navBorder}`, paddingTop: 14 }}>
            <div className="text-xs font-bold uppercase tracking-widest mb-2.5" style={{ color: textSec }}>
              Election Guides
            </div>
            <div className="grid grid-cols-2 gap-2">
              {ELECTION_GUIDES.map((item) => (
                <button
                  key={item.path}
                  onClick={() => { navigate(item.path); setMenuOpen(false); }}
                  data-testid={`mobile-election-${item.path.replace(/\//g, "")}`}
                  className="flex items-start gap-2.5 px-3 py-3 rounded-xl text-left transition-all"
                  style={{
                    minHeight: 56,
                    background: isActive(item.path)
                      ? (isDark ? "rgba(16,185,129,0.12)" : "rgba(13,92,58,0.07)")
                      : (isDark ? "rgba(30,41,59,0.6)" : "rgba(241,245,249,0.8)"),
                    border: `1px solid ${isActive(item.path)
                      ? item.color + "55"
                      : (isDark ? "rgba(51,65,85,0.4)" : "rgba(226,232,240,0.8)")}`,
                  }}
                >
                  <div
                    className="w-1 rounded-full flex-shrink-0 mt-0.5"
                    style={{ height: 32, backgroundColor: item.color }}
                  />
                  <div>
                    <div className="text-xs font-bold leading-tight" style={{ color: textColor }}>{item.label}</div>
                    <div className="text-xs mt-0.5 leading-tight" style={{ color: textSec }}>{item.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* ── Politician Accountability ── */}
          <div className="px-4 pb-3" style={{ borderTop: `1px solid ${navBorder}`, paddingTop: 14 }}>
            <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: textSec }}>
              Politician Accountability
            </div>
            {TOOLS.filter((t) => ACCOUNTABILITY_TOOLS.includes(t.path)).map((tool) => (
              <button
                key={tool.path}
                onClick={() => { navigate(tool.path); setMenuOpen(false); }}
                data-testid={`mobile-tool-${tool.path.slice(1).replace(/\//g, "-")}`}
                className="w-full flex items-center justify-between px-3 rounded-lg transition-all"
                style={{
                  minHeight: 48,
                  background: isActive(tool.path) ? (isDark ? "rgba(16,185,129,0.1)" : "rgba(13,92,58,0.06)") : "transparent",
                  color: isActive(tool.path) ? (isDark ? "#34d399" : "#0D5C3A") : textColor,
                }}
              >
                <span className="text-sm font-medium">{tool.label}</span>
                <span className="text-xs" style={{ color: textSec }}>{tool.desc}</span>
              </button>
            ))}
          </div>

          {/* ── Research Tools ── */}
          <div className="px-4 pb-3" style={{ borderTop: `1px solid ${navBorder}`, paddingTop: 14 }}>
            <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: textSec }}>
              Research Tools
            </div>
            {TOOLS.filter((t) => RESEARCH_TOOLS.includes(t.path)).map((tool) => (
              <button
                key={tool.path}
                onClick={() => { navigate(tool.path); setMenuOpen(false); }}
                data-testid={`mobile-tool-${tool.path.slice(1).replace(/\//g, "-")}`}
                className="w-full flex items-center justify-between px-3 rounded-lg transition-all"
                style={{
                  minHeight: 48,
                  background: isActive(tool.path) ? (isDark ? "rgba(16,185,129,0.1)" : "rgba(13,92,58,0.06)") : "transparent",
                  color: isActive(tool.path) ? (isDark ? "#34d399" : "#0D5C3A") : textColor,
                }}
              >
                <span className="text-sm font-medium">{tool.label}</span>
                <span className="text-xs" style={{ color: textSec }}>{tool.desc}</span>
              </button>
            ))}
          </div>

          {/* ── User / Login CTA ── */}
          <div className="px-4 pt-3 pb-5" style={{ borderTop: `1px solid ${navBorder}` }}>
            {user ? (
              <button
                onClick={() => { navigate("/dashboard"); setMenuOpen(false); }}
                data-testid="mobile-nav-dashboard"
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-sm transition-all"
                style={{
                  background: "linear-gradient(135deg, #0D5C3A 0%, #10B981 100%)",
                  color: "#fff",
                  boxShadow: "0 4px 14px rgba(13,92,58,0.28)",
                }}
              >
                {user.picture
                  ? <img src={user.picture} alt="" className="w-5 h-5 rounded-full" />
                  : <User style={{ width: 15, height: 15 }} />}
                My Dashboard
              </button>
            ) : (
              <button
                onClick={() => { navigate("/auth"); setMenuOpen(false); }}
                data-testid="mobile-nav-login"
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-sm transition-all"
                style={{
                  background: "linear-gradient(135deg, #0D5C3A 0%, #10B981 100%)",
                  color: "#fff",
                  boxShadow: "0 4px 14px rgba(13,92,58,0.28)",
                }}
              >
                <LogIn style={{ width: 15, height: 15 }} />
                Login / Register
              </button>
            )}
          </div>

        </div>
      </div>
    </header>
  );
}
